import { useState, useEffect, useCallback } from "react";
import { LogOut, CheckCircle, XCircle, Mail, Users, Clock, BarChart2, ChevronDown, X, Send, Eye, Search, Settings, Key, UserPlus, Trash2, Shield } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function api(path, opts = {}) {
  const token = localStorage.getItem("admin_token");
  return fetch(`${API}${path}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...opts,
  }).then(async (r) => {
    const text = await r.text().catch(() => "");
    let data = {};
    try { data = JSON.parse(text); } catch {}
    if (!r.ok) throw new Error(data.detail || text || "Request failed");
    return data;
  });
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await api("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      localStorage.setItem("admin_token", data.token);
      onLogin(data.email, data.role || "admin");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#060A16" }}>
      <div style={{ background: "rgba(10,17,40,0.9)", border: "1px solid rgba(212,175,55,0.2)" }} className="w-full max-w-md p-10">
        <div className="text-center mb-8">
          <p className="overline-gold text-[0.65rem] mb-3">Restricted Access</p>
          <h1 className="font-['Cormorant_Garamond'] text-3xl text-white font-medium">Admin Portal</h1>
          <p className="text-[#94A3B8] text-xs font-['Outfit'] mt-2">EU–India Policy Dialogue</p>
          <div className="gold-divider w-32 mx-auto mt-4" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div>
            <label className="form-label">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              className="gold-input w-full"
            />
          </div>
          <div>
            <label className="form-label">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              required
              className="gold-input w-full"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-red-400 text-xs font-['Outfit'] text-center border border-red-400/20 px-3 py-2">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="btn-gold w-full justify-center mt-2"
          >
            {loading ? "Verifying..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="glass-card p-5 flex items-center gap-4">
      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: `${color}18`, border: `1px solid ${color}40` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className="text-[#94A3B8] text-xs font-['Outfit'] uppercase tracking-wider">{label}</p>
        <p className="font-['Cormorant_Garamond'] text-2xl text-white font-semibold leading-tight">{value}</p>
      </div>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    approved: { color: "#22C55E", label: "Approved" },
    rejected: { color: "#EF4444", label: "Rejected" },
    pending: { color: "#D4AF37", label: "Pending" },
  };
  const { color, label } = map[status] || map.pending;
  return (
    <span className="text-[0.6rem] font-['Outfit'] tracking-wider uppercase px-2 py-0.5" style={{ color, border: `1px solid ${color}40`, background: `${color}12` }}>
      {label}
    </span>
  );
}

// ── Reply Modal ───────────────────────────────────────────────────────────────
function ReplyModal({ reg, onClose, onSent }) {
  const [subject, setSubject] = useState(`Re: EU–India Policy Dialogue — ${reg.name}`);
  const [message, setMessage] = useState(
    `Dear ${reg.name},\n\nThank you for your interest in the EU–India Special Policy & Business Dialogue.\n\n\n\nWarm regards,\nOrganising Team\nEU–India Policy Dialogue`
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return;
    setLoading(true);
    setError("");
    try {
      await api(`/api/admin/reply/${reg.id}`, {
        method: "POST",
        body: JSON.stringify({ subject, message }),
      });
      onSent();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }}>
      <div data-testid="reply-modal" className="glass-card w-full max-w-2xl p-8 flex flex-col gap-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="overline-gold text-[0.6rem] mb-1">Compose Reply</p>
            <h3 className="font-['Cormorant_Garamond'] text-xl text-white font-medium">
              To: {reg.name}
            </h3>
            <p className="text-[#94A3B8] text-xs font-['Outfit']">{reg.email}</p>
          </div>
          <button data-testid="reply-modal-close" onClick={onClose} className="text-[#94A3B8] hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="gold-divider" />

        <div>
          <label className="form-label">Subject</label>
          <input
            data-testid="reply-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="gold-input w-full"
          />
        </div>

        <div>
          <label className="form-label">Message</label>
          <textarea
            data-testid="reply-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={10}
            className="gold-input w-full resize-none"
            style={{ borderBottom: "none", border: "1px solid rgba(212,175,55,0.25)", padding: "12px", borderRadius: "2px" }}
          />
        </div>

        {error && (
          <p data-testid="reply-error" className="text-xs font-['Outfit'] px-3 py-2 border" style={{ color: error.includes("not yet configured") ? "#D4AF37" : "#EF4444", borderColor: error.includes("not yet configured") ? "rgba(212,175,55,0.3)" : "rgba(239,68,68,0.3)" }}>
            {error.includes("not yet configured")
              ? "ZeptoMail not yet configured. Add ZEPTOMAIL_API_KEY & ZEPTOMAIL_FROM_EMAIL to .env to enable sending."
              : error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="btn-outline-gold text-sm px-5 py-2.5">Cancel</button>
          <button
            data-testid="reply-send-btn"
            onClick={handleSend}
            disabled={loading}
            className="btn-gold text-sm px-5 py-2.5"
          >
            <Send size={13} />
            {loading ? "Sending..." : "Send Reply"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Row Detail Drawer ─────────────────────────────────────────────────────────
function DetailDrawer({ reg, onClose }) {
  const fields = [
    ["Name", reg.name],
    ["Email", reg.email],
    ["Phone", reg.phone],
    ["Company", reg.company],
    ["Designation", reg.designation],
    ["City", reg.city],
    ["Country", reg.country],
    ["Message", reg.message || "—"],
    ["Submitted", new Date(reg.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })],
    ["Status", reg.status || "pending"],
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div
        data-testid="detail-drawer"
        className="w-full max-w-sm h-full overflow-y-auto p-8 flex flex-col gap-4"
        style={{ background: "#0A1128", borderLeft: "1px solid rgba(212,175,55,0.2)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-['Cormorant_Garamond'] text-xl text-white font-medium">Registration Detail</h3>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white"><X size={18} /></button>
        </div>
        <div className="gold-divider" />
        {fields.map(([label, value]) => (
          <div key={label}>
            <p className="overline-gold text-[0.55rem] mb-0.5">{label}</p>
            <p className="text-[#CBD5E1] text-sm font-['Outfit'] break-words">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Settings Panel ───────────────────────────────────────────────────────────
function SettingsPanel({ adminEmail, adminRole, showToast }) {
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState("");

  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newUserPwd, setNewUserPwd] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState("");

  const [adminUsers, setAdminUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});

  const isSuperAdmin = adminRole === "superadmin";

  const loadUsers = useCallback(async () => {
    if (!isSuperAdmin) return;
    setUsersLoading(true);
    try {
      const users = await api("/api/admin/users");
      setAdminUsers(users);
    } catch { }
    setUsersLoading(false);
  }, [isSuperAdmin]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (newPwd !== confirmPwd) { setPwdError("New passwords do not match"); return; }
    if (newPwd.length < 6) { setPwdError("Password must be at least 6 characters"); return; }
    setPwdLoading(true);
    try {
      await api("/api/admin/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password: curPwd, new_password: newPwd }),
      });
      showToast("Password changed successfully");
      setCurPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      setPwdError(err.message);
    }
    setPwdLoading(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserError("");
    if (newUserPwd.length < 6) { setUserError("Password must be at least 6 characters"); return; }
    setUserLoading(true);
    try {
      await api("/api/admin/create-user", {
        method: "POST",
        body: JSON.stringify({ email: newUserEmail, password: newUserPwd, name: newUserName }),
      });
      showToast(`Admin user ${newUserEmail} created`);
      setNewUserEmail(""); setNewUserName(""); setNewUserPwd("");
      loadUsers();
    } catch (err) {
      setUserError(err.message);
    }
    setUserLoading(false);
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Delete admin user ${email}?`)) return;
    setDeleteLoading((p) => ({ ...p, [email]: true }));
    try {
      await api(`/api/admin/users/${encodeURIComponent(email)}`, { method: "DELETE" });
      showToast(`Deleted ${email}`);
      loadUsers();
    } catch (err) {
      showToast(err.message, "error");
    }
    setDeleteLoading((p) => ({ ...p, [email]: false }));
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Change Password */}
      <div className="glass-card p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Key size={18} className="text-[#D4AF37]" />
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-lg text-white font-medium">Change Password</h3>
            <p className="text-[#94A3B8] text-xs font-['Outfit']">{adminEmail}</p>
          </div>
        </div>
        <form onSubmit={handleChangePassword} className="flex flex-col gap-4 max-w-md">
          <div>
            <label className="form-label">Current Password</label>
            <input type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} required className="gold-input w-full" placeholder="••••••••" />
          </div>
          <div>
            <label className="form-label">New Password</label>
            <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required className="gold-input w-full" placeholder="Min 6 characters" />
          </div>
          <div>
            <label className="form-label">Confirm New Password</label>
            <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required className="gold-input w-full" placeholder="Repeat new password" />
          </div>
          {pwdError && <p className="text-red-400 text-xs font-['Outfit'] border border-red-400/20 px-3 py-2">{pwdError}</p>}
          <button type="submit" disabled={pwdLoading} className="btn-gold w-fit text-sm px-5 py-2.5">
            <Key size={13} /> {pwdLoading ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>

      {/* Super Admin: Create User + User List */}
      {isSuperAdmin && (
        <>
          <div className="glass-card p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <UserPlus size={18} className="text-[#D4AF37]" />
              <h3 className="font-['Cormorant_Garamond'] text-lg text-white font-medium">Create Admin User</h3>
            </div>
            <form onSubmit={handleCreateUser} className="flex flex-col gap-4 max-w-md">
              <div>
                <label className="form-label">Name</label>
                <input value={newUserName} onChange={(e) => setNewUserName(e.target.value)} className="gold-input w-full" placeholder="Full name" />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} required className="gold-input w-full" placeholder="user@example.com" />
              </div>
              <div>
                <label className="form-label">Password *</label>
                <input type="password" value={newUserPwd} onChange={(e) => setNewUserPwd(e.target.value)} required className="gold-input w-full" placeholder="Min 6 characters" />
              </div>
              {userError && <p className="text-red-400 text-xs font-['Outfit'] border border-red-400/20 px-3 py-2">{userError}</p>}
              <button type="submit" disabled={userLoading} className="btn-gold w-fit text-sm px-5 py-2.5">
                <UserPlus size={13} /> {userLoading ? "Creating..." : "Create Admin"}
              </button>
            </form>
          </div>

          <div className="glass-card p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <Shield size={18} className="text-[#D4AF37]" />
              <h3 className="font-['Cormorant_Garamond'] text-lg text-white font-medium">Admin Users</h3>
            </div>
            {usersLoading ? (
              <p className="text-[#94A3B8] text-sm">Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: "1px solid rgba(212,175,55,0.1)" }}>
                      {["Email", "Name", "Role", "Created", ""].map((h) => (
                        <th key={h} className="px-4 py-3 text-left overline-gold text-[0.55rem]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((u) => (
                      <tr key={u.email} style={{ borderBottom: "1px solid rgba(212,175,55,0.06)" }}>
                        <td className="px-4 py-3 text-[#CBD5E1]">{u.email}</td>
                        <td className="px-4 py-3 text-[#94A3B8]">{u.name || "—"}</td>
                        <td className="px-4 py-3">
                          <span className="text-[0.6rem] font-['Outfit'] tracking-wider uppercase px-2 py-0.5"
                            style={{
                              color: u.role === "superadmin" ? "#D4AF37" : "#94A3B8",
                              border: `1px solid ${u.role === "superadmin" ? "rgba(212,175,55,0.4)" : "rgba(148,163,184,0.3)"}`,
                            }}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}
                        </td>
                        <td className="px-4 py-3">
                          {u.email !== adminEmail && u.role !== "superadmin" && (
                            <button
                              onClick={() => handleDeleteUser(u.email)}
                              disabled={deleteLoading[u.email]}
                              title="Delete user"
                              className="w-7 h-7 flex items-center justify-center text-[#EF4444] hover:text-white transition-colors border border-transparent hover:border-[#EF4444]/40 hover:bg-[#EF4444]/10 disabled:opacity-40"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
function Dashboard({ adminEmail, adminRole, onLogout }) {
  const [activeTab, setActiveTab] = useState("registrations");
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [replyTarget, setReplyTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api("/api/admin/stats"),
        api("/api/admin/registrations"),
      ]);
      setStats(s);
      setRegistrations(r);
    } catch (err) {
      if (err.message.includes("Token") || err.message.includes("401")) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateStatus = async (reg, newStatus) => {
    setActionLoading((p) => ({ ...p, [reg.id]: true }));
    try {
      await api(`/api/admin/registrations/${reg.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setRegistrations((prev) => prev.map((r) => r.id === reg.id ? { ...r, status: newStatus } : r));
      setStats((s) => s ? {
        ...s,
        approved: newStatus === "approved" ? s.approved + 1 : (reg.status === "approved" ? s.approved - 1 : s.approved),
        rejected: newStatus === "rejected" ? s.rejected + 1 : (reg.status === "rejected" ? s.rejected - 1 : s.rejected),
        pending: newStatus === "pending" ? s.pending + 1 : s.pending - (reg.status === "pending" ? 1 : 0),
      } : s);
      showToast(`${reg.name} marked as ${newStatus}`);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setActionLoading((p) => ({ ...p, [reg.id]: false }));
    }
  };

  const filtered = registrations
    .filter((r) => filter === "all" || (r.status || "pending") === filter)
    .filter((r) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return [r.name, r.email, r.company, r.city, r.country].some((v) => v?.toLowerCase().includes(q));
    });

  return (
    <div className="min-h-screen font-['Outfit']" style={{ background: "#060A16" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 px-6 py-4 flex items-center justify-between" style={{ background: "rgba(6,10,22,0.95)", borderBottom: "1px solid rgba(212,175,55,0.15)", backdropFilter: "blur(12px)" }}>
        <div>
          <p className="overline-gold text-[0.6rem]">Admin Portal</p>
          <h1 className="font-['Cormorant_Garamond'] text-xl text-white font-medium leading-tight">EU–India Policy Dialogue</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-[#94A3B8] text-xs hidden sm:block">{adminEmail}</span>
          <button
            onClick={() => setActiveTab(activeTab === "settings" ? "registrations" : "settings")}
            className={`flex items-center gap-2 transition-colors text-xs uppercase tracking-wider ${activeTab === "settings" ? "text-[#D4AF37]" : "text-[#94A3B8] hover:text-[#D4AF37]"}`}
          >
            <Settings size={14} /> Settings
          </button>
          <button data-testid="admin-logout-btn" onClick={onLogout} className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-wider">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col gap-8">
        {activeTab === "settings" ? (
          <SettingsPanel adminEmail={adminEmail} adminRole={adminRole} showToast={showToast} />
        ) : (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Submitted" value={stats?.total ?? "—"} color="#D4AF37" testid="stat-total" />
          <StatCard icon={Clock} label="Pending Review" value={stats?.pending ?? "—"} color="#D4AF37" testid="stat-pending" />
          <StatCard icon={CheckCircle} label="Approved" value={stats?.approved ?? "—"} color="#22C55E" testid="stat-approved" />
          <StatCard icon={XCircle} label="Rejected" value={stats?.rejected ?? "—"} color="#EF4444" testid="stat-rejected" />
        </div>

        {/* Table panel */}
        <div className="glass-card overflow-hidden">
          {/* Panel header */}
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" style={{ borderBottom: "1px solid rgba(212,175,55,0.12)" }}>
            <div>
              <p className="overline-gold text-[0.6rem] mb-1">Registrations</p>
              <p className="text-white text-sm font-medium">{filtered.length} record{filtered.length !== 1 ? "s" : ""}</p>
            </div>

            {/* Search */}
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, company…"
                className="pl-8 pr-4 py-2 text-xs bg-[#0A1128] border border-[#D4AF37]/15 text-white placeholder-[#94A3B8] outline-none focus:border-[#D4AF37]/40 w-60"
              />
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex" style={{ borderBottom: "1px solid rgba(212,175,55,0.12)" }}>
            {["all", "pending", "approved", "rejected"].map((tab) => (
              <button
                key={tab}
                data-testid={`filter-${tab}`}
                onClick={() => setFilter(tab)}
                className="flex-1 sm:flex-none px-5 py-3 text-[0.65rem] uppercase tracking-wider transition-colors"
                style={{
                  color: filter === tab ? "#D4AF37" : "#94A3B8",
                  borderBottom: filter === tab ? "2px solid #D4AF37" : "2px solid transparent",
                  background: filter === tab ? "rgba(212,175,55,0.05)" : "transparent",
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Table */}
          {loading ? (
            <div className="py-20 text-center text-[#94A3B8] text-sm">Loading registrations…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="no-registrations" className="py-20 text-center text-[#94A3B8] text-sm">No registrations found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="registrations-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(212,175,55,0.1)" }}>
                    {["Name & Company", "Contact", "Location", "Status", "Date", "Actions"].map((h) => (
                      <th key={h} className="px-4 py-3 text-left overline-gold text-[0.55rem] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((reg) => (
                    <tr
                      key={reg.id}
                      data-testid={`reg-row-${reg.id}`}
                      className="transition-colors"
                      style={{ borderBottom: "1px solid rgba(212,175,55,0.06)" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "rgba(212,175,55,0.03)"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{reg.name}</p>
                        <p className="text-[#94A3B8] mt-0.5">{reg.company}</p>
                        <p className="text-[#64748B] mt-0.5">{reg.designation}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-[#CBD5E1]">{reg.email}</p>
                        <p className="text-[#94A3B8] mt-0.5">{reg.phone}</p>
                      </td>
                      <td className="px-4 py-3 text-[#94A3B8] whitespace-nowrap">
                        {reg.city}, {reg.country}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={reg.status || "pending"} />
                      </td>
                      <td className="px-4 py-3 text-[#64748B] whitespace-nowrap">
                        {new Date(reg.created_at).toLocaleDateString("en-IN")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* View detail */}
                          <button
                            data-testid={`view-btn-${reg.id}`}
                            onClick={() => setDetailTarget(reg)}
                            title="View details"
                            className="w-7 h-7 flex items-center justify-center text-[#94A3B8] hover:text-[#D4AF37] transition-colors border border-transparent hover:border-[#D4AF37]/30"
                          >
                            <Eye size={13} />
                          </button>

                          {/* Approve */}
                          {(reg.status || "pending") !== "approved" && (
                            <button
                              data-testid={`approve-btn-${reg.id}`}
                              onClick={() => updateStatus(reg, "approved")}
                              disabled={actionLoading[reg.id]}
                              title="Approve"
                              className="w-7 h-7 flex items-center justify-center text-[#22C55E] hover:text-white transition-colors border border-transparent hover:border-[#22C55E]/40 hover:bg-[#22C55E]/10 disabled:opacity-40"
                            >
                              <CheckCircle size={13} />
                            </button>
                          )}

                          {/* Reject */}
                          {(reg.status || "pending") !== "rejected" && (
                            <button
                              data-testid={`reject-btn-${reg.id}`}
                              onClick={() => updateStatus(reg, "rejected")}
                              disabled={actionLoading[reg.id]}
                              title="Reject"
                              className="w-7 h-7 flex items-center justify-center text-[#EF4444] hover:text-white transition-colors border border-transparent hover:border-[#EF4444]/40 hover:bg-[#EF4444]/10 disabled:opacity-40"
                            >
                              <XCircle size={13} />
                            </button>
                          )}

                          {/* Reply */}
                          <button
                            data-testid={`reply-btn-${reg.id}`}
                            onClick={() => setReplyTarget(reg)}
                            title="Send reply email"
                            className="w-7 h-7 flex items-center justify-center text-[#94A3B8] hover:text-[#D4AF37] transition-colors border border-transparent hover:border-[#D4AF37]/30"
                          >
                            <Mail size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}
      </main>

      {/* Modals */}
      {replyTarget && (
        <ReplyModal
          reg={replyTarget}
          onClose={() => setReplyTarget(null)}
          onSent={() => {
            setReplyTarget(null);
            showToast(`Reply sent to ${replyTarget.name}`);
          }}
        />
      )}

      {detailTarget && (
        <DetailDrawer reg={detailTarget} onClose={() => setDetailTarget(null)} />
      )}

      {/* Toast */}
      {toast && (
        <div
          data-testid="admin-toast"
          className="fixed bottom-6 right-6 z-50 px-5 py-3 text-sm font-['Outfit'] shadow-lg"
          style={{
            background: toast.type === "error" ? "#1C0A0A" : "#0A1C0A",
            border: `1px solid ${toast.type === "error" ? "rgba(239,68,68,0.4)" : "rgba(34,197,94,0.4)"}`,
            color: toast.type === "error" ? "#EF4444" : "#22C55E",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── Entry Point ───────────────────────────────────────────────────────────────
export default function AdminPage() {
  const [adminData, setAdminData] = useState(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.exp * 1000 < Date.now()) { localStorage.removeItem("admin_token"); return null; }
      return { email: payload.email, role: payload.role || "admin" };
    } catch {
      return null;
    }
  });

  const handleLogin = (email, role) => setAdminData({ email, role });
  const handleLogout = () => { localStorage.removeItem("admin_token"); setAdminData(null); };

  if (!adminData) return <LoginScreen onLogin={handleLogin} />;
  return <Dashboard adminEmail={adminData.email} adminRole={adminData.role} onLogout={handleLogout} />;
}
